import React, { useEffect, useMemo } from 'react';
import { Dropdown, Icon } from 'semantic-ui-react';
import { GetCostCenter } from 'src/graphql/costCenter';
import CostCenterOptionLabel from '../../documentostributarios/costCenters/CostCenterOptionLabel';
import AddDashedOutlineButton from '../../general/js/forms/reconcile/contabilidad/AddDashedOutlineButton';
import '../../general/js/forms/reconcile/contabilidad/JournalEntryCostCentersPanel.scss';
import '../createRuleModal/CreateRuleModal.scss';

const CONFIG_CC_URL = '/configuracion#centros-costo';

const newCcRow = () => ({
  _localId: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  costCenterId: '',
  percent: '100',
});

const RuleCostCenterAllocationsEditor = ({
  companyId,
  value = [],
  onChange,
  error,
  modalOpen = true,
}) => {
  const { data, fetching, reexecuteQuery } = GetCostCenter(companyId);
  const centers = useMemo(
    () => (data?.getManyCostCenter || []).filter((c) => !c.isHidden),
    [data],
  );

  useEffect(() => {
    if (modalOpen && companyId) {
      reexecuteQuery({ requestPolicy: 'network-only' });
    }
  }, [modalOpen, companyId, reexecuteQuery]);

  useEffect(() => {
    const onFocus = () => {
      if (modalOpen && companyId) {
        reexecuteQuery({ requestPolicy: 'network-only' });
      }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [modalOpen, companyId, reexecuteQuery]);

  const options = useMemo(
    () =>
      centers.map((c) => ({
        key: c.id,
        value: c.id,
        text: c.name,
        content: (
          <CostCenterOptionLabel name={c.name} color={c.color} icon={c.icon} />
        ),
      })),
    [centers],
  );

  const rows = value || [];

  const updateRows = (next) => onChange(next);

  const updateRow = (localId, patch) => {
    updateRows(rows.map((r) => (r._localId === localId ? { ...r, ...patch } : r)));
  };

  const addRow = () => updateRows([...rows, newCcRow()]);

  const removeRow = (localId) => {
    updateRows(rows.filter((r) => r._localId !== localId));
  };

  const sumPercent = rows.reduce((s, r) => s + (Number(r.percent) || 0), 0);
  const hasFilled = rows.some((r) => r.costCenterId || r.percent !== '');
  const atFullAllocation =
    rows.length > 0 && Math.abs(sumPercent - 100) <= 0.01;

  return (
    <div className="create-rule-field create-rule-exceptions-section">
      <label className="create-rule-label">CENTROS DE COSTO (OPCIONAL)</label>
      <div className="create-rule-hint create-rule-hint--block">
        Reparto por % sobre el neto de la línea de la cuenta de la regla en el asiento. Si agregas filas, deben sumar 100%.
      </div>
      {rows.map((row) => (
        <div key={row._localId} className="create-rule-exception-card">
          <div className="create-rule-exception-rut">
            <label className="create-rule-sublabel" htmlFor={`cc-${row._localId}`}>
              Centro de costo
            </label>
            <Dropdown
              id={`cc-${row._localId}`}
              placeholder="Seleccionar centro de costo"
              selection
              search
              fluid
              loading={fetching}
              options={options}
              value={row.costCenterId || ''}
              noResultsMessage={
                !fetching && centers.length === 0
                  ? 'No hay centros de costo configurados'
                  : 'Sin resultados'
              }
              onChange={(_, { value: v }) => updateRow(row._localId, { costCenterId: v })}
            />
          </div>
          <div className="create-rule-exception-row-2">
            <div className="create-rule-exception-account">
              <label className="create-rule-sublabel" htmlFor={`cc-pct-${row._localId}`}>
                Porcentaje %
              </label>
              <input
                id={`cc-pct-${row._localId}`}
                type="number"
                min="0"
                max="100"
                step="0.01"
                className="create-rule-input"
                placeholder="50%"
                value={row.percent}
                onChange={(e) => updateRow(row._localId, { percent: e.target.value })}
              />
            </div>
            <div className="create-rule-exception-remove-wrap">
              <span className="create-rule-sublabel create-rule-sublabel--spacer" aria-hidden="true">
                &nbsp;
              </span>
              <button
                type="button"
                className="create-rule-exception-remove"
                onClick={() => removeRow(row._localId)}
                title="Quitar centro de costo"
                aria-label="Quitar centro de costo"
              >
                <Icon name="trash" />
              </button>
            </div>
          </div>
        </div>
      ))}
      <p className="journal-entry-cc-help-text">
        Si falta un centro de costo, créalo en configuración.{' '}
        <a
          href={CONFIG_CC_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="journal-entry-cc-help-link"
        >
          Gestionar centros de costo
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12" aria-hidden>
            <line x1="5" y1="19" x2="19" y2="5" />
            <polyline points="7 5 19 5 19 17" />
          </svg>
        </a>
      </p>
      {error && <div className="create-rule-error">{error}</div>}
      {hasFilled && (
        <div
          className={`create-rule-hint ${Math.abs(sumPercent - 100) > 0.01 ? 'create-rule-error' : ''}`}
          style={{ marginBottom: 8 }}
        >
          Total: {sumPercent.toFixed(2)}%
        </div>
      )}
      <AddDashedOutlineButton
        onClick={addRow}
        disabled={atFullAllocation}
        className="create-rule-add-exception"
      >
        Agregar centro de costo
      </AddDashedOutlineButton>
    </div>
  );
};

export default RuleCostCenterAllocationsEditor;

export function normalizeCostCenterAllocationsForApi(rows) {
  const filled = (rows || []).filter((r) => r.costCenterId && r.percent !== '');
  if (!filled.length) return undefined;
  return filled.map((r) => ({
    costCenterId: r.costCenterId,
    percent: Number(r.percent),
  }));
}

export function costCenterAllocationsFromRule(rule) {
  const raw = rule?.costCenterAllocations;
  if (!raw?.length) return [];
  return raw.map((a) => ({
    _localId: `${a.costCenterId}-${a.percent}-${Math.random().toString(36).slice(2, 7)}`,
    costCenterId: a.costCenterId,
    percent: String(a.percent),
  }));
}
